import React from 'react';

interface ScoreArcProps {
  score: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const ScoreArc: React.FC<ScoreArcProps> = ({
  score,
  size = 120,
  strokeWidth = 14,
  className = ''
}) => {
  // Validate and sanitize the score input
  const validScore = (() => {
    if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) {
      console.warn('ScoreArc received invalid score:', score);
      return 0;
    }
    return Math.max(0, Math.min(10, score));
  })();

  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const normalizedScore = validScore / 10;
  const arcLength = circumference * 0.75;
  const offset = arcLength * (1 - normalizedScore);

  const getScoreColor = (score: number): string => {
    if (score >= 7) return '#22c55e';   // Green for 7-10
    if (score >= 5) return '#f59e0b';   // Amber for 5-6.9
    if (score >= 3) return '#ef4444';   // Red for 3-4.9
    return '#6b7280';                   // Gray for 1-2.9
  };

  const startAngle = -225;
  const endAngle = 45;
  const startX = center + radius * Math.cos((startAngle * Math.PI) / 180);
  const startY = center + radius * Math.sin((startAngle * Math.PI) / 180);
  const endX = center + radius * Math.cos((endAngle * Math.PI) / 180);
  const endY = center + radius * Math.sin((endAngle * Math.PI) / 180);

  const pathData = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${endX} ${endY}`;

  return (
    <div className={`score-arc ${className}`} style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <path
          d={pathData}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        <path
          d={pathData}
          fill="none"
          stroke={getScoreColor(validScore)}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${arcLength}px ${circumference}px`}
          strokeDashoffset={`${offset}px`}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div style={{ marginTop: '8px', textAlign: 'center' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937' }}>
          {validScore.toFixed(1)}
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>Score</div>
      </div>
    </div>
  );
};

export default ScoreArc;